import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const googleCloud = defineConnector({
  identifier: "googleCloud",
  name: "Google Cloud",
  tagLine: "Google Cloud Platform services",
  keywords: "gcp googleapis platform iam service account workspace delegation",
  logo,
  protocol: "http",
  tags: ["featured", "it", "engineering"],
  defaultUrlPattern: () => "https://w*[^w/.][^/]*\\.googleapis\\.com/.*",
  auth: customAuth("googleCloud"),
  steps: [
    {
      title: "Create a service account",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [Google Cloud Console](https://console.cloud.google.com/iam-admin/serviceaccounts) and create a new service account, or use an existing one.\n\nGrant the service account the necessary roles for the APIs you want to use.",
        },
      ],
    },
    {
      title: "Generate a service account key",
      items: [
        {
          kind: "markdown",
          content:
            "In the service account details, go to the **Keys** tab and click **Add Key** → **Create new key**. Choose **JSON** format.\n\nThe key file will be downloaded automatically. Open it and copy the entire JSON contents.",
        },
      ],
    },
    {
      title: "Enter your service account key",
      items: [
        {
          kind: "input",
          label: "Service account key (JSON)",
          placeholder: '{"type": "service_account", "project_id": "...", ...}',
          required: true,
          target: { kind: "secrets", field: "serviceAccountKey" },
          helperText: "Paste the entire JSON key file contents.",
        },
      ],
    },
    {
      title: "Domain-wide delegation",
      items: [
        {
          kind: "select",
          label: "User delegation",
          target: { kind: "configuration", field: "delegationMode" },
          options: [
            { value: "", label: "None" },
            { value: "static", label: "Specific user" },
            { value: "dynamic", label: "Dynamic (set per-request)" },
          ],
          defaultValue: "",
          helperText:
            "Impersonate a Google Workspace user via domain-wide delegation.",
        },
        {
          kind: "input",
          label: "Delegation email",
          placeholder: "user@example.com",
          target: { kind: "configuration", field: "delegationSubject" },
          helperText: "The email of the user to impersonate.",
          visibleWhen: {
            target: { kind: "configuration", field: "delegationMode" },
            value: "static",
          },
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "https://www.googleapis.com/auth/gmail.readonly",
          target: { kind: "configuration", field: "scope" },
          helperText:
            "Space-separated OAuth scopes. Must match the scopes granted in the Google Workspace Admin Console.",
          visibleWhen: {
            target: { kind: "configuration", field: "delegationMode" },
          },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://cloudresourcemanager.googleapis.com/v1/projects",
      expectedStatus: [200, 403],
    }),
  },
  usageHint: ({ delegationMode }) => {
    if (delegationMode !== "dynamic") return undefined;
    return "This connector uses domain-wide delegation with dynamic user impersonation. To set which user to act as, include the header x-3b-delegation-subject with their email (e.g. user@company.com).";
  },
  publicEnvVars: ({ delegationMode, delegationSubject, scope }) => {
    const mode = delegationMode;
    if (!mode) return {} as Record<string, string>;
    const vars: Record<string, string> = {};
    if (mode === "static" && delegationSubject) {
      vars.GCP_DELEGATION_SUBJECT = delegationSubject;
    }
    if (scope) {
      vars.GCP_DELEGATION_SCOPE = scope;
    }
    return vars;
  },
});
